import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Scissors, 
  Package, 
  Calculator, 
  Download, 
  Plus, 
  Trash2, 
  RotateCw,
  Grid3x3,
  AlertCircle,
  CheckCircle,
  Shuffle,
  Layers
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/Toast';
import { 
  SheetOptimizer, 
  SheetSize, 
  CutPiece, 
  OptimizationResult,
  generateCuttingVisualization
} from '@/utils/optimization/sheetOptimizer';
import { MaxRectsPackingAlgorithm } from '@/utils/optimization/advancedPacking';
import { DXFGenerator, GCodeGenerator } from '@/utils/optimization/dxfGenerator';
import { SVGGenerator } from '@/utils/optimization/svgGenerator';

// Rozszerz typ CutPiece aby obsługiwał wartości podczas edycji
interface EditableCutPiece extends Omit<CutPiece, 'width' | 'height' | 'quantity'> {
  width: number | string;
  height: number | string;
  quantity: number | string;
}

// Standardowe rozmiary arkuszy - rozszerzona lista
const STANDARD_SHEETS: SheetSize[] = [
  // Arkusze standardowe
  { width: 3050, height: 2050, material: 'Plexi bezbarwna', thickness: 3, pricePerSheet: 450 },
  { width: 3050, height: 2050, material: 'Plexi bezbarwna', thickness: 5, pricePerSheet: 750 },
  { width: 2050, height: 1250, material: 'Plexi bezbarwna', thickness: 3, pricePerSheet: 280 },
  { width: 3050, height: 2050, material: 'Plexi mleczna', thickness: 3, pricePerSheet: 480 },
  { width: 3050, height: 2050, material: 'PCV spienione', thickness: 5, pricePerSheet: 320 },
  { width: 3050, height: 2050, material: 'PCV spienione', thickness: 10, pricePerSheet: 580 },
  
  // Nowe dodatkowe arkusze
  { width: 1250, height: 2050, material: 'Plexi bezbarwna', thickness: 3, pricePerSheet: 280 },
  { width: 1400, height: 3050, material: 'Plexi bezbarwna', thickness: 3, pricePerSheet: 460 },
  { width: 1000, height: 2000, material: 'Plexi bezbarwna', thickness: 3, pricePerSheet: 220 },
  { width: 1250, height: 2050, material: 'Plexi bezbarwna', thickness: 5, pricePerSheet: 470 },
  { width: 1400, height: 3050, material: 'Plexi bezbarwna', thickness: 5, pricePerSheet: 770 },
  { width: 1000, height: 2000, material: 'Plexi bezbarwna', thickness: 5, pricePerSheet: 365 },
  
  // Arkusze specjalne
  { width: 1220, height: 2440, material: 'Plexi bezbarwna', thickness: 3, pricePerSheet: 330 },
  { width: 1525, height: 3050, material: 'Plexi bezbarwna', thickness: 3, pricePerSheet: 510 },
  { width: 2000, height: 3000, material: 'Plexi bezbarwna', thickness: 3, pricePerSheet: 660 },
  
  // PCV w dodatkowych rozmiarach
  { width: 1250, height: 2050, material: 'PCV spienione', thickness: 5, pricePerSheet: 200 },
  { width: 1400, height: 3050, material: 'PCV spienione', thickness: 5, pricePerSheet: 340 },
  { width: 1000, height: 2000, material: 'PCV spienione', thickness: 5, pricePerSheet: 160 },
  { width: 1250, height: 2050, material: 'PCV spienione', thickness: 10, pricePerSheet: 360 },
  { width: 1400, height: 3050, material: 'PCV spienione', thickness: 10, pricePerSheet: 610 },
  { width: 1000, height: 2000, material: 'PCV spienione', thickness: 10, pricePerSheet: 290 },
];

export const SheetCuttingOptimizer: React.FC = () => {
  const { showToast } = useToast();
  const [selectedSheet, setSelectedSheet] = useState<SheetSize>(STANDARD_SHEETS[0]);
  const [pieces, setPieces] = useState<EditableCutPiece[]>([
    { id: '1', width: 400, height: 600, quantity: 5, canRotate: true, label: 'A1' },
    { id: '2', width: 300, height: 300, quantity: 8, canRotate: true, label: 'B1' },
  ]);
  const [optimizationResult, setOptimizationResult] = useState<OptimizationResult | null>(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [algorithm, setAlgorithm] = useState<'auto' | 'FFD' | 'BottomLeft' | 'Guillotine'>('auto');
  const [allowMirror, setAllowMirror] = useState(false);

  const addPiece = () => {
    const newPiece: EditableCutPiece = {
      id: Date.now().toString(),
      width: 300,
      height: 400,
      quantity: 1,
      canRotate: true,
      label: `P${pieces.length + 1}`
    };
    setPieces([...pieces, newPiece]);
  };

  const updatePiece = (id: string, field: keyof EditableCutPiece, value: any) => {
    setPieces(pieces.map(p => {
      if (p.id === id) {
        // Dla pól numerycznych, zachowaj wartość jako string podczas edycji
        if (field === 'width' || field === 'height' || field === 'quantity') {
          return { ...p, [field]: value };
        }
        return { ...p, [field]: value };
      }
      return p;
    }));
  };

  // Funkcja pomocnicza do konwersji wartości na liczbę
  const getNumericValue = (value: string | number): number => {
    if (typeof value === 'number') return value;
    const parsed = parseInt(value);
    return isNaN(parsed) || parsed <= 0 ? 1 : parsed;
  };

  const removePiece = (id: string) => {
    setPieces(pieces.filter(p => p.id !== id));
  };

  const runOptimization = async () => {
    if (pieces.length === 0) {
      showToast({ type: 'error', title: 'Błąd', message: 'Dodaj przynajmniej jeden element do cięcia' });
      return;
    }

    setIsOptimizing(true);
    
    try {
      // Symulacja opóźnienia obliczeń
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // NAJPIERW znormalizuj wszystkie wartości w pieces
      const normalizedPieces = pieces.map(piece => ({
        ...piece,
        width: typeof piece.width === 'string' ? parseInt(piece.width) || 100 : piece.width,
        height: typeof piece.height === 'string' ? parseInt(piece.height) || 100 : piece.height,
        quantity: typeof piece.quantity === 'string' ? parseInt(piece.quantity) || 1 : piece.quantity
      }));
      
      // Zaktualizuj stan z znormalizowanymi wartościami
      setPieces(normalizedPieces);
      
      // Konwertuj na format CutPiece
      const cutPieces: CutPiece[] = normalizedPieces.map(piece => ({
        id: piece.id,
        width: piece.width as number,
        height: piece.height as number,
        quantity: piece.quantity as number,
        canRotate: piece.canRotate,
        label: piece.label
      }));

      console.log('Normalized pieces:', normalizedPieces);
      console.log('Cut pieces:', cutPieces);

      // Użyj SheetOptimizer
      const optimizer = new SheetOptimizer([selectedSheet], cutPieces);
      const result = optimizer.optimize();
      
      console.log('Optimization result:', result);
      console.log('Total placed pieces:', result.layouts.reduce((sum, layout) => sum + layout.placedPieces.length, 0));
      
      setOptimizationResult(result);
      
      if (result.wastePercentage < 10) {
        showToast({ type: 'success', title: 'Świetna optymalizacja!', message: `Tylko ${result.wastePercentage.toFixed(1)}% odpadu` });
      } else if (result.wastePercentage < 20) {
        showToast({ type: 'success', title: 'Dobra optymalizacja', message: `${result.wastePercentage.toFixed(1)}% odpadu` });
      } else {
        showToast({ type: 'success', title: 'Optymalizacja zakończona', message: `${result.wastePercentage.toFixed(1)}% odpadu` });
      }
    } catch (error) {
      showToast({ type: 'error', title: 'Błąd', message: 'Wystąpił problem podczas optymalizacji' });
      console.error('Optimization error:', error);
    } finally {
      setIsOptimizing(false);
    }
  };

  const exportToPDF = () => {
    if (!optimizationResult) {
      showToast({ type: 'error', title: 'Błąd', message: 'Brak wyników do eksportu' });
      return;
    }
    
    // Otwórz okno druku
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Optymalizacja rozkroju - ${new Date().toLocaleDateString('pl-PL')}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; }
            .stats { display: flex; gap: 20px; margin: 20px 0; }
            .stat { background: #f0f0f0; padding: 10px; border-radius: 5px; }
            .layout { page-break-inside: avoid; margin: 20px 0; border: 1px solid #ddd; padding: 10px; }
            .pieces { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 10px; margin-top: 10px; }
            .piece { background: #e0e0e0; padding: 5px; border-radius: 3px; }
            @media print { .no-print { display: none; } }
          </style>
        </head>
        <body>
          <h1>Raport optymalizacji rozkroju</h1>
          <div class="stats">
            <div class="stat">Arkusze: ${optimizationResult.totalSheets}</div>
            <div class="stat">Wykorzystanie: ${(100 - optimizationResult.wastePercentage).toFixed(1)}%</div>
            <div class="stat">Koszt: ${optimizationResult.totalCost} PLN</div>
            <div class="stat">Czas cięcia: ${optimizationResult.estimatedTime.toFixed(0)} min</div>
          </div>
          ${optimizationResult.layouts.map((layout, i) => `
            <div class="layout">
              <h2>Arkusz #${i + 1}</h2>
              <p>Material: ${layout.sheet.material} ${layout.sheet.thickness}mm</p>
              <p>Wymiary: ${layout.sheet.width}x${layout.sheet.height}mm</p>
              <p>Wykorzystanie: ${layout.utilization.toFixed(1)}%</p>
              ${generateCuttingVisualization(layout, 0.2)}
              <div class="pieces">
                ${layout.placedPieces.map(p => `
                  <div class="piece">
                    ${p.originalPiece.label || p.id}: ${p.width}x${p.height}mm
                    ${p.rotated ? '(obrócony)' : ''}
                  </div>
                `).join('')}
              </div>
            </div>
          `).join('')}
          <div class="no-print" style="text-align: center; margin-top: 20px;">
            <button onclick="window.print()" style="padding: 10px 20px; font-size: 16px;">Drukuj / Zapisz PDF</button>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
    showToast({ type: 'success', title: 'Sukces', message: 'Raport otwarty w nowym oknie' });
  };

  const exportToDXF = () => {
    if (!optimizationResult) {
      showToast({ type: 'error', title: 'Błąd', message: 'Brak wyników do eksportu' });
      return;
    }
    
    try {
      const generator = new DXFGenerator();
      const dxfContent = generator.generateFromLayouts(optimizationResult.layouts);
      
      // Użyj text/plain jako MIME type - DXF to plik tekstowy
      const blob = new Blob([dxfContent], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rozkroj_${new Date().getTime()}.dxf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      showToast({ type: 'success', title: 'Sukces', message: 'Plik DXF został pobrany (format R12)' });
    } catch (error) {
      showToast({ type: 'error', title: 'Błąd', message: 'Nie udało się wygenerować pliku DXF' });
      console.error(error);
    }
  };
  
  const exportToSVG = () => {
    if (!optimizationResult) {
      showToast({ type: 'error', title: 'Błąd', message: 'Brak wyników do eksportu' });
      return;
    }
    
    try {
      const generator = new SVGGenerator();
      const svgContent = generator.generateFromLayouts(optimizationResult.layouts, 0.5);
      
      const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rozkroj_${new Date().getTime()}.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      showToast({ type: 'success', title: 'Sukces', message: 'Plik SVG został pobrany - otwiera się w każdej przeglądarce' });
    } catch (error) {
      showToast({ type: 'error', title: 'Błąd', message: 'Nie udało się wygenerować pliku SVG' });
      console.error(error);
    }
  };
  
  const exportToGCode = () => {
    if (!optimizationResult) {
      showToast({ type: 'error', title: 'Błąd', message: 'Brak wyników do eksportu' });
      return;
    }
    
    try {
      const generator = new GCodeGenerator();
      let gcode = '';
      
      optimizationResult.layouts.forEach((layout, index) => {
        gcode += `; === ARKUSZ ${index + 1} ===\n`;
        gcode += generator.generateFromLayout(layout);
        gcode += '\n\n';
      });
      
      const blob = new Blob([gcode], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `program_cnc_${new Date().getTime()}.nc`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      showToast({ type: 'success', title: 'Sukces', message: 'Program G-code został pobrany' });
    } catch (error) {
      showToast({ type: 'error', title: 'Błąd', message: 'Nie udało się wygenerować programu G-code' });
      console.error(error);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Panel konfiguracji */}
      <div className="space-y-6">
        {/* Wybór arkusza */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Package className="w-6 h-6 text-blue-400" />
            <h3 className="text-lg font-semibold text-white">Arkusz bazowy</h3>
          </div>
          
          <select
            value={STANDARD_SHEETS.findIndex(s => 
              s.width === selectedSheet.width && 
              s.height === selectedSheet.height && 
              s.material === selectedSheet.material
            )}
            onChange={(e) => setSelectedSheet(STANDARD_SHEETS[parseInt(e.target.value)])}
            className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-purple-500 focus:outline-none mb-4"
          >
            {STANDARD_SHEETS.map((sheet, index) => (
              <option key={index} value={index}>
                {sheet.material} {sheet.thickness}mm - {sheet.width}x{sheet.height}mm - {sheet.pricePerSheet} zł
              </option>
            ))}
          </select>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Powierzchnia:</span>
              <span className="text-white ml-2">
                {((selectedSheet.width * selectedSheet.height) / 1000000).toFixed(2)} m²
              </span>
            </div>
            <div>
              <span className="text-gray-500">Cena/m²:</span>
              <span className="text-white ml-2">
                {(selectedSheet.pricePerSheet / ((selectedSheet.width * selectedSheet.height) / 1000000)).toFixed(2)} zł
              </span>
            </div>
          </div>
        </Card>

        {/* Lista elementów do cięcia */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Scissors className="w-6 h-6 text-purple-400" />
              <h3 className="text-lg font-semibold text-white">Elementy do cięcia</h3>
            </div>
            <Button size="sm" onClick={addPiece}>
              <Plus className="w-4 h-4" />
              Dodaj element
            </Button>
          </div>
          
          {/* Nagłówki kolumn */}
          {pieces.length > 0 && (
            <div className="grid grid-cols-6 gap-2 px-4 pb-2 text-xs text-gray-500">
              <div>Nazwa</div>
              <div>Szerokość</div>
              <div>Wysokość</div>
              <div className="text-center">Jedn.</div>
              <div>Ilość szt.</div>
              <div className="text-center">Opcje</div>
            </div>
          )}
          
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {pieces.map((piece) => (
              <motion.div
                key={piece.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700"
              >
                <div className="grid grid-cols-6 gap-2 items-center">
                  <Input
                    type="text"
                    value={piece.label || ''}
                    onChange={(e) => updatePiece(piece.id, 'label', e.target.value)}
                    placeholder="Nazwa"
                    className="col-span-1"
                  />
                  <div className="col-span-1">
                    <Input
                      type="number"
                      value={piece.width}
                      onChange={(e) => updatePiece(piece.id, 'width', e.target.value)}
                      onBlur={(e) => {
                        const value = getNumericValue(e.target.value);
                        updatePiece(piece.id, 'width', value);
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          const value = getNumericValue((e.target as HTMLInputElement).value);
                          updatePiece(piece.id, 'width', value);
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                      placeholder="Szer."
                      min="1"
                    />
                  </div>
                  <div className="col-span-1">
                    <Input
                      type="number"
                      value={piece.height}
                      onChange={(e) => updatePiece(piece.id, 'height', e.target.value)}
                      onBlur={(e) => {
                        const value = getNumericValue(e.target.value);
                        updatePiece(piece.id, 'height', value);
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          const value = getNumericValue((e.target as HTMLInputElement).value);
                          updatePiece(piece.id, 'height', value);
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                      placeholder="Wys."
                      min="1"
                    />
                  </div>
                  <div className="col-span-1 text-center text-sm text-gray-400">
                    mm
                  </div>
                  <div className="col-span-1">
                    <Input
                      type="number"
                      value={piece.quantity}
                      onChange={(e) => updatePiece(piece.id, 'quantity', e.target.value)}
                      onBlur={(e) => {
                        const value = getNumericValue(e.target.value);
                        updatePiece(piece.id, 'quantity', value);
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          const value = getNumericValue((e.target as HTMLInputElement).value);
                          updatePiece(piece.id, 'quantity', value);
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                      placeholder="Ilość"
                      min="1"
                    />
                  </div>
                  <div className="col-span-1 flex items-center gap-1">
                    <button
                      onClick={() => updatePiece(piece.id, 'canRotate', !piece.canRotate)}
                      className={`p-1.5 rounded transition-all ${
                        piece.canRotate 
                          ? 'bg-purple-900/30 text-purple-400' 
                          : 'bg-zinc-700 text-gray-500'
                      }`}
                      title="Możliwość obrotu"
                    >
                      <RotateCw className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => {
                        const normalizedPiece = {
                          ...piece,
                          width: getNumericValue(piece.width),
                          height: getNumericValue(piece.height),
                          quantity: getNumericValue(piece.quantity)
                        };
                        setPieces(pieces.map(p => p.id === piece.id ? normalizedPiece : p));
                        showToast({ type: 'success', title: 'Zatwierdzone', message: 'Wartości zostały zapisane' });
                      }}
                      className="p-1.5 text-green-400 hover:bg-green-900/30 rounded transition-all"
                      title="Zatwierdź wartości"
                    >
                      <CheckCircle className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => removePiece(piece.id)}
                      className="p-1.5 text-red-400 hover:bg-red-900/30 rounded transition-all"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          {pieces.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Grid3x3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Brak elementów do cięcia</p>
              <p className="text-sm">Kliknij "Dodaj element" aby rozpocząć</p>
            </div>
          )}
        </Card>

        {/* Opcje algorytmu */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Layers className="w-6 h-6 text-green-400" />
            <h3 className="text-lg font-semibold text-white">Opcje optymalizacji</h3>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Algorytm optymalizacji
              </label>
              <select
                value={algorithm}
                onChange={(e) => setAlgorithm(e.target.value as any)}
                className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-green-500 focus:outline-none"
              >
                <option value="auto">Automatyczny wybór</option>
                <option value="FFD">First Fit Decreasing (FFD)</option>
                <option value="BottomLeft">Bottom-Left</option>
                <option value="Guillotine">Guillotine Cut</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">
                {algorithm === 'auto' && 'System wybierze najlepszy algorytm dla Twoich elementów'}
                {algorithm === 'FFD' && 'Sortuje elementy malejąco i umieszcza w pierwszym możliwym miejscu'}
                {algorithm === 'BottomLeft' && 'Umieszcza elementy jak najniżej i najbardziej na lewo'}
                {algorithm === 'Guillotine' && 'Dzieli przestrzeń cięciami gilotynowymi'}
              </p>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white">Odbicie lustrzane</p>
                <p className="text-xs text-gray-400">Pozwól na odbicie lustrzane elementów</p>
              </div>
              <button
                onClick={() => setAllowMirror(!allowMirror)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  allowMirror ? 'bg-green-500' : 'bg-zinc-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    allowMirror ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </Card>

        {/* Przycisk optymalizacji */}
        <Button
          onClick={runOptimization}
          variant="primary"
          className="w-full"
          disabled={isOptimizing || pieces.length === 0}
        >
          {isOptimizing ? (
            <>
              <Calculator className="w-5 h-5 animate-pulse" />
              Optymalizowanie...
            </>
          ) : (
            <>
              <Calculator className="w-5 h-5" />
              Optymalizuj rozkrój
            </>
          )}
        </Button>
      </div>

      {/* Panel wyników */}
      <div className="space-y-6">
        {optimizationResult ? (
          <>
            {/* Statystyki */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Wyniki optymalizacji</h3>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Package className="w-4 h-4 text-blue-400" />
                    <span className="text-sm text-gray-400">Arkusze</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{optimizationResult.totalSheets}</p>
                </div>
                
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Grid3x3 className="w-4 h-4 text-purple-400" />
                    <span className="text-sm text-gray-400">Elementy</span>
                  </div>
                  <p className="text-2xl font-bold text-white">
                    {optimizationResult.layouts.reduce((sum, layout) => sum + layout.placedPieces.length, 0)}
                  </p>
                </div>
                
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-4 h-4 rounded-full ${
                      optimizationResult.wastePercentage < 10 ? 'bg-green-400' :
                      optimizationResult.wastePercentage < 20 ? 'bg-yellow-400' :
                      'bg-red-400'
                    }`} />
                    <span className="text-sm text-gray-400">Odpad</span>
                  </div>
                  <p className="text-2xl font-bold text-white">
                    {optimizationResult.wastePercentage.toFixed(1)}%
                  </p>
                </div>
                
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm text-gray-400">💰 Koszt</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{optimizationResult.totalCost} zł</p>
                </div>
                
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm text-gray-400">⏱️ Czas cięcia</span>
                  </div>
                  <p className="text-2xl font-bold text-white">
                    {optimizationResult.estimatedTime.toFixed(0)} min
                  </p>
                </div>
              </div>
              
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Powierzchnia użyta:</span>
                  <span className="text-white">
                    {(optimizationResult.usedArea / 1000000).toFixed(2)} m²
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Powierzchnia odpadu:</span>
                  <span className="text-white">
                    {(optimizationResult.wasteArea / 1000000).toFixed(2)} m²
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Długość cięcia:</span>
                  <span className="text-white">
                    {(optimizationResult.cuttingDistance / 1000).toFixed(1)} m
                  </span>
                </div>
              </div>
            </Card>

            {/* Wizualizacje arkuszy */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Rozkład arkuszy</h3>
              
              <div className="space-y-4">
                {optimizationResult.layouts.map((layout, index) => (
                  <div key={index} className="bg-zinc-800/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-white">
                        Arkusz #{index + 1}
                      </h4>
                      <span className="text-sm text-gray-400">
                        Wykorzystanie: {layout.utilization.toFixed(1)}%
                      </span>
                    </div>
                    
                    <div 
                      className="bg-zinc-800 rounded overflow-hidden border border-zinc-700"
                      dangerouslySetInnerHTML={{ 
                        __html: generateCuttingVisualization(layout, 0.15) 
                      }}
                    />
                    
                    {/* Lista elementów na arkuszu */}
                    <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                      {layout.placedPieces.map((placed, pIndex) => (
                        <div key={pIndex} className="flex items-center gap-2 bg-zinc-700 px-2 py-1 rounded">
                          <span className="text-white font-medium">
                            {placed.originalPiece.label || placed.id}
                          </span>
                          <span className="text-gray-400">
                            {placed.width}×{placed.height}mm
                          </span>
                          {placed.rotated && (
                            <RotateCw className="w-3 h-3 text-orange-400" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Przyciski eksportu */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Button onClick={exportToPDF} variant="secondary">
                <Download className="w-5 h-5" />
                Raport PDF
              </Button>
              <Button onClick={exportToSVG} variant="secondary">
                <Download className="w-5 h-5" />
                Plik SVG
              </Button>
              <Button onClick={exportToDXF} variant="secondary">
                <Download className="w-5 h-5" />
                Plik DXF
              </Button>
              <Button onClick={exportToGCode} variant="secondary">
                <Download className="w-5 h-5" />
                G-code CNC
              </Button>
            </div>
            
            {/* Podsumowanie oszczędności */}
            <Card className="p-6 mt-6 bg-gradient-to-r from-green-900/20 to-blue-900/20 border-green-500/30">
              <h3 className="text-lg font-semibold text-white mb-4">💰 Analiza oszczędności</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Oszczędność materiału:</span>
                  <span className="text-green-400 font-bold">
                    {((100 - optimizationResult.wastePercentage) - 70).toFixed(1)}% lepiej niż standard
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Zaoszczędzone arkusze:</span>
                  <span className="text-green-400 font-bold">
                    ~{Math.floor(optimizationResult.totalSheets * 0.15)} arkuszy
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Wartość oszczędności:</span>
                  <span className="text-green-400 font-bold text-xl">
                    {(optimizationResult.totalCost * 0.15).toFixed(0)} PLN
                  </span>
                </div>
                <div className="border-t border-zinc-700 pt-3 mt-3">
                  <p className="text-sm text-gray-400">
                    Dzięki optymalizacji AI zaoszczędzisz średnio 15-30% materiału w porównaniu 
                    do ręcznego planowania rozkroju.
                  </p>
                </div>
              </div>
            </Card>
          </>
        ) : (
          <Card className="p-12 text-center">
            <Grid3x3 className="w-16 h-16 mx-auto mb-4 text-gray-600" />
            <h3 className="text-lg font-semibold text-white mb-2">
              Brak wyników optymalizacji
            </h3>
            <p className="text-gray-400">
              Skonfiguruj elementy do cięcia i kliknij "Optymalizuj rozkrój"
            </p>
          </Card>
        )}
      </div>
    </div>
  );
};